'use client'

import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { DateSelectArg, DatesSetArg, DayCellContentArg } from '@fullcalendar/core'
import plLocale from '@fullcalendar/core/locales/pl'
import { useTranslation } from 'react-i18next'
import { Button } from '@planner/ui'
import { useRouter } from 'next/navigation'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { format, isSameDay, isSameMonth, startOfDay, startOfMonth } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useClientCalendar, type CalendarAvailability } from '../hooks/useClientCalendar'
import { ReservationForm } from './ReservationForm'
import './CalendarStyles.css'

export function ClientCalendarView() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const calendarRef = useRef<FullCalendar>(null)
  
  // HYDRATION FIX: Always start with false for SSR
  // Update to actual value in useEffect
  const [isMobile, setIsMobile] = useState(false)
  
  const [currentDate, setCurrentDate] = useState(() => {
    return startOfMonth(new Date())
  })
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [reservationFormOpen, setReservationFormOpen] = useState(false)
  
  const { availability, loading, error } = useClientCalendar(currentDate, 'dayGridMonth')

  // Track if we've forced the initial date update
  const hasForcedInitialDate = useRef(false)

  // Ensure calendar shows current month on mount - use useLayoutEffect for immediate execution
  useLayoutEffect(() => {
    if (calendarRef.current && !hasForcedInitialDate.current) {
      const calendarApi = calendarRef.current.getApi()
      const now = new Date()
      const targetDate = startOfMonth(now)
      
      // Always force update to current month on mount
      console.log('[ClientCalendarView] Forcing initial update to current month:', {
        targetDate: targetDate.toISOString(),
        now: now.toISOString()
      })
      calendarApi.gotoDate(targetDate)
      setCurrentDate(targetDate)
      hasForcedInitialDate.current = true
    }
  }, []) // Only run on mount

  // Detect mobile on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Create a map of availability by date for quick lookup
  const availabilityMap = new Map<string, CalendarAvailability>()
  availability.forEach(item => {
    availabilityMap.set(item.date, item)
  })

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const selectedDateObj = new Date(selectInfo.start)
    const dateStr = format(selectedDateObj, 'yyyy-MM-dd')
    const avail = availabilityMap.get(dateStr)
    
    // Only allow selection of free or pending (own) days
    if (avail && (avail.status === 'free' || (avail.status === 'pending' && avail.is_own_event))) {
      setSelectedDate(selectedDateObj)
      setReservationFormOpen(true)
    }
  }

  const handleNavigate = (direction: 'prev' | 'next' | 'today') => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      if (direction === 'prev') {
        calendarApi.prev()
        setCurrentDate(calendarApi.getDate())
      } else if (direction === 'next') {
        calendarApi.next()
        setCurrentDate(calendarApi.getDate())
      } else {
        // When clicking "today", always reset to current month
        const now = new Date()
        const newDate = startOfMonth(now)
        calendarApi.gotoDate(newDate)
        setCurrentDate(newDate)
      }
    }
  }

  const handleDatesSet = (dateInfo: DatesSetArg) => {
    const newDate = dateInfo.start
    
    // On first load, check if we need to force update to current month
    if (!hasForcedInitialDate.current) {
      const now = new Date()
      const targetDate = startOfMonth(now)
      
      if (!isSameMonth(newDate, targetDate) && calendarRef.current) {
        console.log('[ClientCalendarView] DatesSet: Forcing update to current month:', {
          calendarDate: newDate.toISOString(),
          targetDate: targetDate.toISOString(),
          now: now.toISOString()
        })
        const calendarApi = calendarRef.current.getApi()
        calendarApi.gotoDate(targetDate)
        setCurrentDate(targetDate)
        hasForcedInitialDate.current = true
        return
      }
      hasForcedInitialDate.current = true
    }
    
    if (!currentDate || !isSameMonth(currentDate, newDate)) {
      setCurrentDate(newDate)
    }
  }

  // Custom day cell content to show status
  const dayCellContent = (dayInfo: DayCellContentArg) => {
    const dateStr = format(dayInfo.date, 'yyyy-MM-dd')
    const avail = availabilityMap.get(dateStr)
    
    if (!avail) {
      return <div className="fc-daygrid-day-number">{dayInfo.dayNumberText}</div>
    }

    let statusClass = ''
    let statusText = ''
    let statusColor = ''

    if (avail.status === 'busy') {
      statusClass = 'fc-day-busy'
      statusText = 'Zajęty'
      statusColor = 'bg-red-100 dark:bg-red-900/20 border-red-300 dark:border-red-700'
    } else if (avail.status === 'pending' && avail.is_own_event) {
      statusClass = 'fc-day-pending'
      statusText = 'Oczekuje na potwierdzenie'
      statusColor = 'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
    } else {
      statusClass = 'fc-day-free'
      statusText = 'Wolny'
      statusColor = 'bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-700'
    }

    // Only show status for days in current month
    const isCurrentMonth = isSameMonth(dayInfo.date, currentDate)
    
    return (
      <div className="fc-daygrid-day-frame">
        <div className="fc-daygrid-day-number">{dayInfo.dayNumberText}</div>
        {isCurrentMonth && (
          <div className={`fc-day-status ${statusClass} ${statusColor} text-xs px-1 py-0.5 rounded mt-1 border`}>
            {statusText}
          </div>
        )}
      </div>
    )
  }

  // Custom day cell class names
  const dayCellClassNames = (dayInfo: { date: Date }) => {
    const dateStr = format(dayInfo.date, 'yyyy-MM-dd')
    const avail = availabilityMap.get(dateStr)
    
    if (!avail) {
      return ''
    }

    const classes: string[] = []
    
    if (avail.status === 'busy') {
      classes.push('fc-day-busy')
    } else if (avail.status === 'pending' && avail.is_own_event) {
      classes.push('fc-day-pending')
      classes.push('cursor-pointer hover:bg-yellow-50 dark:hover:bg-yellow-900/10')
    } else if (avail.status === 'free') {
      classes.push('fc-day-free')
      classes.push('cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/10')
    }

    return classes.join(' ')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">{t('common.loading', 'Ładowanie...')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">
          {t('common.error', 'Błąd')}: {error.message}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">
            {t('calendar.title', 'Kalendarz dostępności')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            {format(currentDate, 'MMMM yyyy', { locale: pl })}
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border bg-background p-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNavigate('prev')}
            className="h-7 md:h-8 text-xs md:text-sm px-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNavigate('today')}
            className="h-7 md:h-8 text-xs md:text-sm px-2 md:px-3"
          >
            {t('calendar.today', 'Dzisiaj')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNavigate('next')}
            className="h-7 md:h-8 text-xs md:text-sm px-2"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded"></div>
          <span className="text-sm">Wolny</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded"></div>
          <span className="text-sm">Zajęty</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded"></div>
          <span className="text-sm">Oczekuje na potwierdzenie</span>
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-sm overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-accent/3 pointer-events-none rounded-lg" />
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={currentDate}
          locale={i18n.language === 'pl' ? plLocale : undefined}
          headerToolbar={false}
          height={isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 400px)'}
          selectable={true}
          select={handleDateSelect}
          datesSet={handleDatesSet}
          firstDay={1} // Monday
          dayCellContent={dayCellContent}
          dayCellClassNames={dayCellClassNames}
          selectMirror={true}
          selectOverlap={false}
          // Only allow selecting free or pending (own) days
          selectAllow={(selectInfo) => {
            const dateStr = format(selectInfo.start, 'yyyy-MM-dd')
            const avail = availabilityMap.get(dateStr)
            return avail ? (avail.status === 'free' || (avail.status === 'pending' && avail.is_own_event)) : false
          }}
        />
      </div>

      <ReservationForm
        selectedDate={selectedDate}
        open={reservationFormOpen}
        onClose={() => {
          setReservationFormOpen(false)
          setSelectedDate(null)
        }}
        onSuccess={() => {
          // Optionally refresh or show success message
        }}
      />
    </div>
  )
}
